/**
 * MUTAGIM.SHOP — Categories List Page
 * Affiche toutes les catégories avec leurs images de couverture
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search, X } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CategoriesListPage() {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandSearch, setBrandSearch] = useState('');
  
  const lang = i18n.language?.split('-')[0] || 'fr';

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.categories || data || []);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const getName = (name) => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    return name[lang] || name.fr || name.en || '';
  };

  const getImageUrl = (category) => {
    // Try cover_image first, then image_url
    const img = category.cover_image || category.image_url;
    if (!img) return null;
    if (img.startsWith('http')) return img;
    if (img.startsWith('/api')) return `${API_URL}${img}`;
    return img;
  };

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="categories-list-page">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-b from-zinc-900 to-mutagim-dark py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t('categories.title', 'Nos Catégories')}
            </h1>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t('categories.subtitle', 'Découvrez notre sélection de produits par catégorie')}
            </p>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="container mx-auto px-4 py-12">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-400">
                {t('categories.noCategories', 'Aucune catégorie disponible')}
              </p>
            </div>
          ) : (
            <div className="space-y-16">
              {/* Section Catégories Produits */}
              {(() => {
                const productCategories = categories.filter(c => c.is_active !== false && c.type !== 'brand');
                if (productCategories.length === 0) return null;
                return (
                  <section>
                    <div className="flex items-center gap-3 mb-8">
                      <span className="text-3xl">📦</span>
                      <h2 className="text-2xl font-bold text-white">
                        {t('categories.productTypes', 'Types de Produits')}
                      </h2>
                      <span className="text-sm text-gray-500 bg-zinc-800 px-3 py-1 rounded-full">
                        {productCategories.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {productCategories.map((category) => (
                        <Link
                          key={category.id || category._id || category.slug}
                          to={`/category/${category.slug}`}
                          className="group relative overflow-hidden rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-mutagim-red transition-all duration-300"
                          data-testid={`category-card-${category.slug}`}
                        >
                          <div className="aspect-[4/3] overflow-hidden bg-zinc-800">
                            {getImageUrl(category) ? (
                              <img
                                src={getImageUrl(category)}
                                alt={getName(category.name)}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
                                <span className="text-6xl">{category.emoji || '📦'}</span>
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <div className="flex items-center gap-2 mb-1">
                              {category.emoji && <span className="text-xl">{category.emoji}</span>}
                              <h3 className="text-lg font-semibold text-white group-hover:text-mutagim-red transition-colors">
                                {getName(category.name)}
                              </h3>
                            </div>
                            {category.products_count !== undefined && (
                              <p className="text-sm text-gray-400">
                                {category.products_count} {t('categories.products', 'produits')}
                              </p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </section>
                );
              })()}
              
              {/* Section Catégories Marques */}
              {(() => {
                // Filtrer les marques actives, trier alphabétiquement, puis filtrer par recherche
                const allBrandCategories = categories
                  .filter(c => c.is_active !== false && c.type === 'brand')
                  .sort((a, b) => {
                    const nameA = getName(a.name).toLowerCase();
                    const nameB = getName(b.name).toLowerCase();
                    return nameA.localeCompare(nameB);
                  });
                
                // Filtrer par recherche
                const brandCategories = brandSearch.trim() 
                  ? allBrandCategories.filter(c => 
                      getName(c.name).toLowerCase().includes(brandSearch.toLowerCase())
                    )
                  : allBrandCategories;
                
                if (allBrandCategories.length === 0) return null;
                return (
                  <section>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">🏷️</span>
                        <h2 className="text-2xl font-bold text-white">
                          {t('categories.brands', 'Nos Marques')}
                        </h2>
                        <span className="text-sm text-gray-500 bg-zinc-800 px-3 py-1 rounded-full">
                          {brandCategories.length}/{allBrandCategories.length}
                        </span>
                      </div>
                      
                      {/* Barre de recherche marques */}
                      <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <Input
                          type="text"
                          placeholder={t('categories.searchBrands', 'Rechercher une marque...')}
                          value={brandSearch}
                          onChange={(e) => setBrandSearch(e.target.value)}
                          className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-gray-500 focus:border-indigo-500"
                          data-testid="brand-search-input"
                        />
                        {brandSearch && (
                          <button
                            onClick={() => setBrandSearch('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                            data-testid="brand-search-clear"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {brandCategories.length === 0 ? (
                      <div className="text-center py-12 bg-zinc-900/30 rounded-xl border border-zinc-800">
                        <p className="text-gray-400">
                          {t('categories.noResults', 'Aucune marque trouvée pour')} "{brandSearch}"
                        </p>
                        <button
                          onClick={() => setBrandSearch('')}
                          className="mt-3 text-indigo-400 hover:text-indigo-300 text-sm"
                        >
                          {t('categories.clearSearch', 'Effacer la recherche')}
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                        {brandCategories.map((category) => (
                          <Link
                            key={category.id || category._id || category.slug}
                            to={`/category/${category.slug}`}
                            className="group relative overflow-hidden rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-indigo-500 transition-all duration-300"
                            data-testid={`brand-card-${category.slug}`}
                          >
                            <div className="aspect-square overflow-hidden bg-zinc-800">
                              {getImageUrl(category) ? (
                                <img
                                  src={getImageUrl(category)}
                                  alt={getName(category.name)}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
                                  <span className="text-4xl font-bold text-white/20">
                                    {getName(category.name)?.charAt(0) || '?'}
                                  </span>
                                </div>
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-3 text-center">
                              <h3 className="text-sm font-semibold text-white group-hover:text-indigo-400 transition-colors truncate">
                                {getName(category.name)}
                              </h3>
                              {category.products_count !== undefined && category.products_count > 0 && (
                                <p className="text-xs text-gray-500">
                                  {category.products_count} {t('categories.products', 'produits')}
                                </p>
                              )}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })()}
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default CategoriesListPage;
